import { db } from './firebase';
import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  getDocs,
  updateDoc,
  doc,
  serverTimestamp,
  Timestamp,
  deleteDoc
} from 'firebase/firestore';

export type NotificationType =
  | 'new_episode'
  | 'new_season'
  | 'show_ended'
  | 'show_cancelled'
  | 'upcoming_episode'
  | 'upcoming_season';

export interface Notification {
  id?: string;
  userId: string;
  type: NotificationType;
  title: string;
  message: string;
  mediaId: number; // TMDB ID
  mediaType: 'tv' | 'movie';
  mediaName: string;
  posterPath: string | null;
  seasonNumber?: number;
  episodeNumber?: number;
  airDate?: string;
  seen: boolean;
  read: boolean;
  createdAt: Timestamp | Date;
  link: string; // URL to navigate to when clicked
}

export class NotificationService {
  /**
   * Create a new notification
   */
  static async createNotification(notification: Omit<Notification, 'id' | 'createdAt'>): Promise<string> {
    try {
      const notificationsRef = collection(db, 'notifications');
      const docRef = await addDoc(notificationsRef, {
        ...notification,
        createdAt: serverTimestamp(),
      });
      return docRef.id;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get all notifications for a user
   */
  static async getUserNotifications(userId: string, limit: number = 50): Promise<Notification[]> {
    try {
      const notificationsRef = collection(db, 'notifications');
      const q = query(
        notificationsRef,
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      const notifications: Notification[] = [];

      snapshot.forEach((doc) => {
        notifications.push({
          id: doc.id,
          ...doc.data(),
        } as Notification);
      });

      return notifications.slice(0, limit);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get unread notification count
   */
  static async getUnreadCount(userId: string): Promise<number> {
    try {
      const notificationsRef = collection(db, 'notifications');
      const q = query(
        notificationsRef,
        where('userId', '==', userId),
        where('read', '==', false)
      );

      const snapshot = await getDocs(q);
      return snapshot.size;
    } catch (error) {
      return 0;
    }
  }

  /**
   * Mark notification as seen (on hover)
   */
  static async markAsSeen(notificationId: string): Promise<void> {
    try {
      const notificationRef = doc(db, 'notifications', notificationId);
      await updateDoc(notificationRef, {
        seen: true,
      });
    } catch (error) {
      throw error;
    }
  }

  /**
   * Mark notification as read (on click)
   */
  static async markAsRead(notificationId: string): Promise<void> {
    try {
      const notificationRef = doc(db, 'notifications', notificationId);
      await updateDoc(notificationRef, {
        read: true,
        seen: true,
      });
    } catch (error) {
      throw error;
    }
  }

  /**
   * Mark all notifications as read
   */
  static async markAllAsRead(userId: string): Promise<void> {
    try {
      const notificationsRef = collection(db, 'notifications');
      const q = query(
        notificationsRef,
        where('userId', '==', userId),
        where('read', '==', false)
      );

      const snapshot = await getDocs(q);
      const updatePromises = snapshot.docs.map((document) =>
        updateDoc(doc(db, 'notifications', document.id), {
          read: true,
          seen: true,
        })
      );

      await Promise.all(updatePromises);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Delete a notification
   */
  static async deleteNotification(notificationId: string): Promise<void> {
    try {
      const notificationRef = doc(db, 'notifications', notificationId);
      await deleteDoc(notificationRef);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Delete old notifications (older than 30 days)
   */
  static async cleanupOldNotifications(userId: string): Promise<void> {
    try {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const notificationsRef = collection(db, 'notifications');
      const q = query(
        notificationsRef,
        where('userId', '==', userId),
        where('createdAt', '<', Timestamp.fromDate(thirtyDaysAgo))
      );

      const snapshot = await getDocs(q);
      const deletePromises = snapshot.docs.map((document) =>
        deleteDoc(doc(db, 'notifications', document.id))
      );

      await Promise.all(deletePromises);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Generate notification message based on type
   */
  static generateNotificationMessage(
    type: NotificationType,
    mediaName: string,
    seasonNumber?: number,
    episodeNumber?: number,
    airDate?: string
  ): { title: string; message: string } {
    switch (type) {
      case 'new_episode':
        return {
          title: 'New Episode Available',
          message: `${mediaName} S${seasonNumber}E${episodeNumber} is now available!`,
        };
      case 'new_season':
        return {
          title: 'New Season Available',
          message: `${mediaName} Season ${seasonNumber} is now available!`,
        };
      case 'show_ended':
        return {
          title: 'Show Ended',
          message: `${mediaName} has ended. The final episodes are now available.`,
        };
      case 'show_cancelled':
        return {
          title: 'Show Cancelled',
          message: `${mediaName} has been cancelled.`,
        };
      case 'upcoming_episode':
        return {
          title: 'Upcoming Episode',
          message: `${mediaName} S${seasonNumber}E${episodeNumber} airs ${airDate}`,
        };
      case 'upcoming_season':
        return {
          title: 'Upcoming Season',
          message: `${mediaName} Season ${seasonNumber} airs ${airDate}`,
        };
      default:
        return {
          title: 'Update',
          message: `${mediaName} has an update`,
        };
    }
  }
}
